import { useQuery } from "@tanstack/react-query";
import { useActualViewer } from "@softmax/web-features/session/records";
import { NavigationReady } from "@softmax/web-features/navigation/markers";
import {
  PlayEmptyNote,
  PlayEpisodeResults,
  PlaySubpage,
} from "@softmax/product-ux/play";
import { useGameContext } from "@/features/game/state";
import type { PlayEpisodesData } from "./lib/types";
import { EpisodesTable } from "./sections/episodes-table";
import { LeagueEpisodesTable } from "./sections/league-episodes-table";
import { SubpageHeader } from "./sections/subpage-header";
import { playEpisodesOptions } from "./play.queries";
import { PlayResource } from "./play-resource";
function Episodes({ data }: { readonly data: PlayEpisodesData }) {
  if (data.kind === "no-policy")
    return (
      <PlayEmptyNote>You have no policy in this league yet.</PlayEmptyNote>
    );

  if (data.kind === "league") {
    if (!data.rows.length)
      return <PlayEmptyNote>No completed episodes yet.</PlayEmptyNote>;

    return (
      <PlayEpisodeResults summary="The league's own latest episodes — not filtered to any one policy.">
        <LeagueEpisodesTable
          rows={data.rows}
          now={data.generatedAt}
          allEpisodesHref={null}
          maxRows={data.rows.length}
        />
      </PlayEpisodeResults>
    );
  }

  if (!data.episodes.summary.loaded)
    return <PlayEmptyNote>No scored episodes in this batch.</PlayEmptyNote>;

  const count = data.episodes.summary.loaded;
  return (
    <PlayEpisodeResults
      summary={`${data.policyLabel} · ${count} scored episode${count === 1 ? "" : "s"} in this batch`}
    >
      <EpisodesTable
        rows={data.episodes.rows}
        summary={data.episodes.summary}
        now={data.generatedAt}
        allEpisodesHref={null}
        maxRows={data.episodes.rows.length}
      />
    </PlayEpisodeResults>
  );
}
export function PlayEpisodesPage() {
  const game = useGameContext();
  const viewer = useActualViewer();
  const query = useQuery({
    ...playEpisodesOptions({
      href: game.publicPath,
      viewerId: viewer.data?.subject_id ?? null,
    }),
    enabled: viewer.isSuccess,
  });
  return (
    <PlayResource query={query} requiresSession>
      {(data) => (
        <PlaySubpage
          header={
            <SubpageHeader
              leagueName={data.league.name}
              route={data.league.route}
              title="Episodes"
            />
          }
        >
          <NavigationReady stage="content" path={game.publicPath} />
          <Episodes data={data} />
        </PlaySubpage>
      )}
    </PlayResource>
  );
}
